/* global globalThis */
// Enkripsi file Disbox (sisi web). Format HARUS identik dengan discord-bot/encryption.js.
//
// - Kunci: PBKDF2-HMAC-SHA256 (200.000 iterasi) dari passphrase, salt
//   "disbox-enc-v1:<userId>", panjang 32 byte -> AES-256-GCM.
// - Tiap chunk dienkripsi TERPISAH: [IV 12 byte][ciphertext][tag 16 byte].
//   Jadi chunk tersimpan = chunk asli + 28 byte (ENC_OVERHEAD).
//
// Halaman yang dibuka lewat http://IP (bukan localhost/https) TIDAK punya
// crypto.subtle, jadi ada cadangan library murni-JS (@noble) dengan format sama.
import { gcm } from '@noble/ciphers/aes.js';
import { pbkdf2 } from '@noble/hashes/pbkdf2.js';
import { sha256 } from '@noble/hashes/sha2.js';

export const ENC_OVERHEAD = 28; // 12 (IV) + 16 (tag GCM)
const IV_LENGTH = 12;
const PBKDF2_ITERATIONS = 200000;
const CHECK_PLAINTEXT = 'disbox-encryption-ok';

const subtle = typeof globalThis.crypto !== 'undefined' && globalThis.crypto.subtle ? globalThis.crypto.subtle : null;

function toBase64(bytes) {
    let binary = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    }
    return btoa(binary);
}

function fromBase64(text) {
    const binary = atob(text);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

// Menurunkan 32 byte kunci mentah dari passphrase (async supaya UI tidak macet
// terlalu lama saat memakai cadangan JS).
export async function deriveKeyBytes(passphrase, userId) {
    const enc = new TextEncoder();
    const salt = enc.encode(`disbox-enc-v1:${userId}`);
    if (subtle) {
        const base = await subtle.importKey('raw', enc.encode(passphrase), 'PBKDF2', false, ['deriveBits']);
        const bits = await subtle.deriveBits(
            { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: PBKDF2_ITERATIONS }, base, 256);
        return new Uint8Array(bits);
    }
    return pbkdf2(sha256, enc.encode(passphrase), salt, { c: PBKDF2_ITERATIONS, dkLen: 32 });
}

export function keyBytesToString(bytes) {
    return toBase64(bytes);
}

export function keyBytesFromString(text) {
    return fromBase64(text);
}

// Bungkus kunci jadi objek dengan encrypt/decrypt untuk satu buffer.
export async function makeCipher(keyBytes) {
    const cryptoKey = subtle ? await subtle.importKey('raw', keyBytes, 'AES-GCM', false, ['encrypt', 'decrypt']) : null;
    return {
        // plain: Uint8Array -> Uint8Array [IV][ciphertext+tag]
        async encrypt(plain) {
            const iv = globalThis.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
            const sealed = cryptoKey
                ? new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv }, cryptoKey, plain))
                : gcm(keyBytes, iv).encrypt(plain);
            const out = new Uint8Array(IV_LENGTH + sealed.length);
            out.set(iv, 0);
            out.set(sealed, IV_LENGTH);
            return out;
        },
        // stored: Uint8Array [IV][ciphertext+tag] -> Uint8Array plaintext (error kalau kunci salah / data rusak)
        async decrypt(stored) {
            if (stored.length < ENC_OVERHEAD) throw new Error('Data terenkripsi terlalu pendek / rusak.');
            const iv = stored.subarray(0, IV_LENGTH);
            const sealed = stored.subarray(IV_LENGTH);
            try {
                return cryptoKey
                    ? new Uint8Array(await subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, sealed))
                    : gcm(keyBytes, iv).decrypt(sealed);
            } catch {
                throw new Error('Gagal mendekripsi: passphrase salah atau data rusak.');
            }
        },
    };
}

export async function encryptBlob(cipher, blob) {
    const plain = new Uint8Array(await blob.arrayBuffer());
    return new Blob([await cipher.encrypt(plain)]);
}

export async function decryptBlob(cipher, blob) {
    const stored = new Uint8Array(await blob.arrayBuffer());
    return new Blob([await cipher.decrypt(stored)]);
}

// Tanda kecil yang disimpan di root index untuk memeriksa apakah passphrase
// yang diketik benar (dan sama dengan yang dipakai bot).
export async function makeCheck(cipher) {
    return toBase64(await cipher.encrypt(new TextEncoder().encode(CHECK_PLAINTEXT)));
}

export async function verifyCheck(cipher, check) {
    try {
        const plain = await cipher.decrypt(fromBase64(check));
        return new TextDecoder().decode(plain) === CHECK_PLAINTEXT;
    } catch {
        return false;
    }
}
